import React, { useEffect, useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useParams } from 'react-router-dom';
import Web3 from "web3";
import ApiCall from '../../utils/Api';
import { toast } from 'react-toastify';
import {convertDateFormat,extractTimeFromTimestamp} from "../../utils/services";

export default function PurchasedDataUnit_DetailsTable() {
  const [web3, setWeb3] = useState(null);
  const {id, buyerId} = useParams(); // Get both dataUnit ID and optional buyer ID
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ethPrice, setEthPrice] = useState(null);
  const [fundTitle, setFundTitle] = useState('');
  const [annualYieldPercent, setAnnualYieldPercent] = useState(0);

  const coinPriceFieldCandidates = useMemo(() => ([
    'coinPriceUSD',
    'coinPriceUsd',
    'coinPriceusd',
    'coin_price_usd',
    'coin_priceUsd',
    'coinPrice',
    'coin_price',
    'priceUSD',
    'priceUsd',
    'price_usd',
    'price'
  ]), []);

  const ticketUsdFieldCandidates = useMemo(() => ([
    'ticketUSD',
    'ticketUsd',
    'ticket_usd',
    'priceUSD',
    'priceUsd',
    'price_usd',
    'ticketPriceUSD',
    'ticket_price_usd',
    'price'
  ]), []);

  const extractNumericValue = (value) => {
    if (value === undefined || value === null) return 0;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  };

  const extractTicketUSD = (primaryLevel, purchase) => {
    const sources = [
      primaryLevel,
      purchase?.selectedLevel,
      purchase,
      purchase?.dataUnit
    ];

    for (const source of sources) {
      if (!source || typeof source !== 'object') continue;
      for (const field of ticketUsdFieldCandidates) {
        if (Object.prototype.hasOwnProperty.call(source, field)) {
          const value = extractNumericValue(source[field]);
          if (value > 0) {
            return value;
          }
        }
      }
    }

    return 0;
  };

  const extractCoinPriceUSD = (purchaseSelectedLevel, purchase, ticketUSD) => {
    const checkObjectForCoinPrice = (obj) => {
      if (!obj || typeof obj !== 'object') return 0;

      for (const field of coinPriceFieldCandidates) {
        if (Object.prototype.hasOwnProperty.call(obj, field)) {
          const value = extractNumericValue(obj[field]);
          if (value > 0) {
            return value;
          }
        }
      }

      // As a heuristic, scan keys that include "coin" and "price"
      for (const key of Object.keys(obj)) {
        const lowerKey = key.toLowerCase();
        if (
          (lowerKey.includes('coin') && lowerKey.includes('price')) ||
          lowerKey.includes('coinprice')
        ) {
          const value = extractNumericValue(obj[key]);
          if (value > 0) {
            return value;
          }
        }
      }

      return 0;
    };

    let coinPriceUSD = checkObjectForCoinPrice(purchaseSelectedLevel);

    if (coinPriceUSD === 0) {
      coinPriceUSD = checkObjectForCoinPrice(purchase?.selectedLevel);
    }

    if (coinPriceUSD === 0 && purchase) {
      coinPriceUSD = checkObjectForCoinPrice(purchase);
    }

    const pricingLevels = purchase?.dataUnit?.pricingLevels;
    if (coinPriceUSD === 0 && Array.isArray(pricingLevels) && pricingLevels.length > 0) {
      if (ticketUSD > 0) {
        const matchedLevel = pricingLevels.find((level) => {
          const levelTicketUSD = extractTicketUSD(level, null);
          if (levelTicketUSD === 0) return false;
          return Math.abs(levelTicketUSD - ticketUSD) < 0.01;
        });

        if (matchedLevel) {
          coinPriceUSD = checkObjectForCoinPrice(matchedLevel);
        }
      }

      if (coinPriceUSD === 0) {
        for (const level of pricingLevels) {
          coinPriceUSD = checkObjectForCoinPrice(level);
          if (coinPriceUSD > 0) {
            break;
          }
        }
      }
    }

    return coinPriceUSD;
  };

  // Fetch ETH price
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiCall.Api.get('/thirdPartyApi/getCryptoData');
        const ethereumData = response.data.data['1027'];
        const usdPrice = ethereumData.quote.USD.price;
        setEthPrice(usdPrice);
      } catch (error) {
        console.error('Error fetching ETH price:', error);
      }
    };
    fetchData();
  }, []);

  // Initialize Web3
  useEffect(() => {
    const initWeb3 = async () => {
      if (window.ethereum) {
        const web3Instance = new Web3(window.ethereum);
        try {
          await window.ethereum.enable();
          setWeb3(web3Instance);
        } catch (error) {
          console.error(error);
        }
      } else if (window.web3) {
        const web3Instance = new Web3(window.web3.currentProvider);
        setWeb3(web3Instance);
      }
    };
    initWeb3();
  }, []);

  // Fetch transaction details
  useEffect(() => {
    const getPurchasedDataUnit = async () => {
      if (!web3) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const data = await ApiCall.SECURE_API.get(`/buyer/getAllBuyers`);
        const allTransactions = data.data.data || [];
        
        // Filter transactions for the specific data unit AND subscriber (if buyerId provided)
        // This shows all transactions that THIS subscriber made for this fund (as a ledger)
        const filteredTransactions = allTransactions.filter(transaction => {
          const dataUnitId = transaction.dataUnit?._id?.toString() || transaction.dataUnit?._id;
          const matchesDataUnit = String(dataUnitId) === String(id);
          
          // If buyerId is provided, also filter by subscriber/buyer ID
          if (buyerId) {
            const transactionBuyerId = transaction?.BuyerConsumer?._id?.toString() || 
                                      transaction?.BuyerConsumer?.toString() || 
                                      transaction?.BuyerConsumer ||
                                      transaction?.BuyerProvider?._id?.toString() || 
                                      transaction?.BuyerProvider?.toString() || 
                                      transaction?.BuyerProvider;
            return matchesDataUnit && String(transactionBuyerId) === String(buyerId);
          }
          
          // If no buyerId, show all transactions for this fund (backward compatibility)
          return matchesDataUnit;
        });

        if (filteredTransactions.length === 0) {
          setTransactions([]);
          setLoading(false);
          return;
        }

        // Get fund name and level name from first transaction
        const firstTransaction = filteredTransactions[0];
        let fundDisplayName = firstTransaction?.fundUnitName || 
                              firstTransaction?.fundName?.replace(/\s*-\s*Multi-Level Fund\s*/i, '') ||
                              firstTransaction?.dataUnit?.name?.replace(/\s*-\s*Multi-Level Fund\s*/i, '') ||
                              'Fund';
        
        // Try to get level name from selectedLevel
        const selectedLevel = firstTransaction?.selectedLevel || {};
        let levelDisplayName = selectedLevel?.levelName || 
                               selectedLevel?.level_name || 
                               selectedLevel?.name;
        
        // If no level name, try to match from pricingLevels
        if (!levelDisplayName && firstTransaction?.dataUnit?.pricingLevels) {
          const matchedLevel = firstTransaction.dataUnit.pricingLevels.find(level => {
            const levelTicketUSD = level.ticketUSD || level.ticket_usd;
            const purchaseTicketUSD = selectedLevel?.ticketUSD;
            return levelTicketUSD && purchaseTicketUSD && Math.abs(levelTicketUSD - purchaseTicketUSD) < 0.01;
          });
          if (matchedLevel) {
            levelDisplayName = matchedLevel.levelName || matchedLevel.level_name || matchedLevel.name || '1';
          } else if (firstTransaction.dataUnit.pricingLevels.length === 1) {
            levelDisplayName = firstTransaction.dataUnit.pricingLevels[0].levelName || 
                               firstTransaction.dataUnit.pricingLevels[0].level_name || 
                               '1';
          }
        }

        // Set title with fund name and level
        if (levelDisplayName) {
          setFundTitle(`${fundDisplayName} - Level${levelDisplayName}`);
        } else {
          setFundTitle(fundDisplayName);
        }

        // Get annual yield percent
        const yieldPercent = selectedLevel?.annualYieldPercent || 
                           firstTransaction?.dataUnit?.pricingLevels?.find(level => 
                             level.levelName === levelDisplayName || 
                             level.level_name === levelDisplayName
                           )?.annualYieldPercent ||
                           firstTransaction?.dataUnit?.pricingLevels?.[0]?.annualYieldPercent ||
                           0;
        setAnnualYieldPercent(yieldPercent);

        // Process each transaction
        const transactionList = filteredTransactions.map((purchase) => {
          const purchaseSelectedLevel = purchase?.selectedLevel || {};
          const ticketUSD = extractTicketUSD(purchaseSelectedLevel, purchase);
          const ticketsPurchased = purchase?.ticketsPurchased || 1;
          const purchaseDate = purchase?.purchaseDate || purchase?.createdAt;
          const coinPriceUSD = extractCoinPriceUSD(purchaseSelectedLevel, purchase, ticketUSD);
          
          // Calculate purchase price in ETH
          let purchasePriceETH = 0;
          if (purchase.totalWei && web3) {
            try {
              purchasePriceETH = parseFloat(web3.utils.fromWei(purchase.totalWei, 'ether'));
            } catch (e) {
              console.error('Error parsing totalWei:', e);
            }
          } else if (purchase.priceWei && web3) {
            try {
              const priceEthPerTicket = parseFloat(web3.utils.fromWei(purchase.priceWei, 'ether'));
              purchasePriceETH = priceEthPerTicket * ticketsPurchased;
            } catch (e) {
              console.error('Error parsing priceWei:', e);
            }
          }
          const purchasePriceUSD = ticketUSD * ticketsPurchased;
          
          let investmentTDUX = 0;
          if (ticketUSD > 0 && coinPriceUSD > 0) {
            investmentTDUX = (ticketUSD / coinPriceUSD) * ticketsPurchased;
          } else if (purchase?.totalCoinQuantity) {
            investmentTDUX = extractNumericValue(purchase.totalCoinQuantity);
          } else if (purchase?.totalTDUX) {
            investmentTDUX = extractNumericValue(purchase.totalTDUX);
          } else {
            investmentTDUX = ticketUSD * ticketsPurchased;
          }
          
          // Calculate yield balance for this specific transaction
          let yieldBalance = 0;
          let yieldAddedToBalance = 0;
          
          if (yieldPercent > 0 && investmentTDUX > 0 && purchaseDate) {
            const currentDate = new Date();
            const purchaseDateObj = new Date(purchaseDate);
            
            // Calculate total annual yield for this transaction
            const totalAnnualYield = (investmentTDUX * yieldPercent) / 100;
            
            // Calculate months elapsed (full months only)
            const monthsElapsed = Math.floor(
              (currentDate.getFullYear() - purchaseDateObj.getFullYear()) * 12 +
              (currentDate.getMonth() - purchaseDateObj.getMonth())
            );
            
            // Monthly payout (1/12th of annual yield)
            const monthlyPayout = totalAnnualYield / 12;
            
            // Yield already added to balance (only full months)
            yieldAddedToBalance = monthlyPayout * Math.max(0, monthsElapsed);
            
            // Remaining yield balance
            yieldBalance = Math.max(0, totalAnnualYield - yieldAddedToBalance);
          }
          
          // Balance = Investment + Yield already added to balance
          const balance = investmentTDUX + yieldAddedToBalance;
          
          // Get transaction hash - ensure we use the purchase transaction hash, not fund creation hash
          // IMPORTANT: Use purchase.transactionHash (purchase transaction), NOT dataUnit.transactionHash (fund creation)
          let transactionHash = purchase?.transactionHash || '';
          
          // Validate that we're not accidentally using the fund creation hash
          // If transactionHash matches dataUnit.transactionHash, it might be the fund creation hash
          const fundCreationHash = purchase?.dataUnit?.transactionHash || '';
          if (transactionHash && fundCreationHash && transactionHash === fundCreationHash) {
            // This might be the fund creation hash, log a warning but still use it
            // (in case the purchase transaction hash is legitimately the same)
            console.warn('Transaction hash matches fund creation hash - this might be incorrect:', transactionHash);
          }
          
          return {
            purchaseDate,
            ticketsPurchased,
            purchasePriceETH,
            purchasePriceUSD,
            investmentTDUX,
            yieldBalance,
            balance,
            transactionHash: transactionHash
          };
        });

        // Sort by purchase date (most recent first) - ensure latest transactions appear first
        transactionList.sort((a, b) => {
          const dateA = new Date(a.purchaseDate);
          const dateB = new Date(b.purchaseDate);
          // Most recent first (descending order)
          return dateB - dateA;
        });
        
        setTransactions(transactionList);
      } catch (error) {
        console.error("Error fetching transaction details:", error);
        setError(error.message || "Failed to load transaction details");
        if (error?.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(error.message || "Failed to load transaction details");
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (web3 && ethPrice) {
      getPurchasedDataUnit();
    }
  }, [id, buyerId, web3, ethPrice]);

  // Expose fundTitle to parent component
  useEffect(() => {
    if (fundTitle && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('fundTitleUpdate', { detail: { fundTitle } }));
    }
  }, [fundTitle]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px',
        fontSize: '16px',
        color: '#666'
      }}>
        Loading transaction details...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px',
        fontSize: '16px',
        color: '#d32f2f',
        flexDirection: 'column',
        gap: '10px'
      }}>
        <div>Error: {error}</div>
        <button 
          onClick={() => window.location.reload()} 
          style={{
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <TableContainer style={{ borderRadius: 9 }} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="fund investment details">
          <TableHead style={{ backgroundColor: "Black" }}>
            <TableRow>
              <TableCell align="center" style={{ color: "white" }}>Purchase Date and Time</TableCell>
              <TableCell align="center" style={{ color: "white" }}># of Tickets</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Purchase Price</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Investment</TableCell>
              <TableCell align="center" style={{ color: "white" }}>Yield</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Yield Balance</TableCell>
              <TableCell align="right" style={{ color: "white" }}>Balance</TableCell>
              <TableCell align="center" style={{ color: "white" }}>Explorer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions && transactions.length > 0 ? (
              transactions.map((transaction, index) => (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell align="center">
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                      <span style={{ fontWeight: 600 }}>
                        {convertDateFormat(transaction.purchaseDate)}
                      </span>
                      <span style={{ color: "gray", fontSize: '12px' }}>
                        {extractTimeFromTimestamp(transaction.purchaseDate)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    {transaction.ticketsPurchased || 0}
                  </TableCell>
                  <TableCell align="right">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <span style={{ fontWeight: 600 }}>
                        {isNaN(transaction.purchasePriceETH) ? '0.0000' : Number(transaction.purchasePriceETH).toFixed(4)} ETH
                      </span>
                      <span style={{ color: 'gray' }}>
                        {isNaN(transaction.purchasePriceUSD) ? '$0.00' : `$${Number(transaction.purchasePriceUSD).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} USD
                      </span>
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    {isNaN(transaction.investmentTDUX) ? '-' : `${Number(transaction.investmentTDUX).toLocaleString()} TDUX`}
                  </TableCell>
                  <TableCell align="center">
                    {annualYieldPercent ? `${annualYieldPercent}%` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {isNaN(transaction.yieldBalance) ? '-' : `${Number(transaction.yieldBalance).toFixed(2)} TDUX`}
                  </TableCell>
                  <TableCell align="right">
                    {isNaN(transaction.balance) ? '-' : `${Number(transaction.balance).toFixed(2)} TDUX`}
                  </TableCell>
                  <TableCell align="center">
                    {transaction.transactionHash ? (
                      <a 
                        target='_blank' 
                        rel="noopener noreferrer"
                        href={`https://sepolia.etherscan.io/tx/${transaction.transactionHash}`}
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                      >
                        Link on Explorer
                      </a>
                    ) : (
                      <span style={{ color: '#666', fontStyle: 'italic' }}>
                        -
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: '40px' }}>
                  <div style={{ fontSize: '16px', color: '#666' }}>
                    No transaction details found for this fund.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
